import { Box } from '@mui/material'
import Head from 'next/head'
import { useRouter } from 'next/router'
import moment from 'moment'
import { getCurrency } from '../../lib/currency'

const MarketPageSeo = ({ filterBy, averageRent }) => {
  const router = useRouter()
  const { asPath, locale = 'en' } = router
  const {
    annual = '',
    avgRent = '',
    // sqft = '',
    updateTime = null,
    annualMeanPriceChange = ''
    // availableApartmentNums = ''
  } = averageRent || {}
  // const { urlPath } = query

  const { city: cityName, area: areaName, cityPath, areaPath } = filterBy
  const locationName = `${areaName} ${areaName ? ',' : ''} ${cityName}`
  const areaRoute = areaPath ? `/${areaPath}` : ''

  const title = `${areaName || cityName} Rental Market Overview and Average Rent Prices | Renty.AI`
  const description = `The average rental cost in ${locationName} is $${
    avgRent && getCurrency(avgRent, 0)
  }. Get more insights on the Los Angeles rental market using Renty.AI.`
  const breadcrumbSchema = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: { name: 'Home', '@id': 'https://renty.ai/' }
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: { name: 'Market Trends', '@id': `https://renty.ai${asPath}` }
      }
    ]
  }
  const schemaData = {
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `As of ${
              updateTime ? moment(updateTime).format('MMMM YYYY') : '--'
            }, the average rent in <a href='https://renty.ai/market-trends/${cityPath}${areaRoute}'> ${locationName} </a> was $${
              getCurrency(avgRent, 0) || '--'
            }/month.`
          },
          name: `What is the average rent in ${locationName}?`
        },
        {
          '@type': 'Question',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `In the past year, rent has increased by ${Number(annual * 100).toFixed(
              2
            )}%, which averages out to $${Math.trunc(annualMeanPriceChange)} ${
              Math.trunc(annualMeanPriceChange) > 0 ? 'more' : 'decrease'
            } per month.`
          },
          name: `How much has average rent changed in ${locationName}`
        }
        // {
        //   '@type': 'Question',
        //   acceptedAnswer: {
        //     '@type': 'Answer',
        //     text: "Based on current rent prices, <a href='https://www.apartments.com/chinatown-seattle-wa/'> Chinatown</a>, <a href='https://www.apartments.com/atlantic-seattle-wa/'> Atlantic</a> and <a href='https://www.apartments.com/north-seattle-seattle-wa/'> North Seattle</a> are the most affordable neighborhoods in Seattle."
        //   },
        //   name: 'What are the most affordable neighborhoods in Seattle?'
        // },
        // {
        //   '@type': 'Question',
        //   acceptedAnswer: {
        //     '@type': 'Answer',
        //     text: "Based on current rent prices, <a href='https://www.apartments.com/south-lake-union-seattle-wa/'> South Lake Union</a>, <a href='https://www.apartments.com/denny-triangle-seattle-wa/'> Denny Triangle</a> and <a href='https://www.apartments.com/lake-union-seattle-wa/'> Lake Union</a> are the most expensive neighborhoods in Seattle."
        //   },
        //   name: 'What are the most expensive neighborhoods in Seattle?'
        // },
        // {
        //   '@type': 'Question',
        //   acceptedAnswer: {
        //     '@type': 'Answer',
        //     text: "The neighborhoods in Seattle with the most apartments available are <a href='https://www.apartments.com/downtown-seattle-seattle-wa/'> Downtown Seattle</a>, <a href='https://www.apartments.com/greater-duwamish-seattle-wa/'> Greater Duwamish</a> and <a href='https://www.apartments.com/central-seattle-seattle-wa/'> Central Seattle</a>."
        //   },
        //   name: 'Which Seattle neighborhoods have the most rentals available?'
        // }
      ],
      '@context': 'http://schema.org'
    },
    breadcrumb: breadcrumbSchema,
    // breadcrumb: [
    //   {
    //     itemListElement: [
    //       {
    //         '@type': 'ListItem',
    //         position: 1,
    //         item: { name: 'Washington', '@id': 'https://www.apartments.com/wa/' }
    //       },
    //       {
    //         '@type': 'ListItem',
    //         position: 2,
    //         item: { name: 'King County', '@id': 'https://www.apartments.com/king-county-wa/' }
    //       },
    //       {
    //         '@type': 'ListItem',
    //         position: 3,
    //         item: { name: 'Seattle', '@id': 'https://www.apartments.com/seattle-wa/' }
    //       },
    //       {
    //         '@type': 'ListItem',
    //         position: 4,
    //         item: { name: `${areaName || cityName} Rental Trends` }
    //       }
    //     ],
    //     '@context': 'http://schema.org',
    //     '@type': 'BreadcrumbList'
    //   }
    // ],
    '@context': 'http://schema.org'
  }

  const schemas = [schemaData]

  return (
    <>
      <Head>
        <title>
          {/* eslint-disable-next-line no-nested-ternary */}
          {title}
        </title>
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta data-n-head="ssr" name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`https://renty.ai${asPath}`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Renty.AI" />
        <meta property="og:image" content="" />
        <meta property="og:image:alt" content={title} />
        <meta property="og:image:width" content="600" />
        <meta property="og:image:height" content="315" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <link
          rel="canonical"
          href={`https://renty.ai${locale === 'en' ? '' : `/${locale}`}${asPath}`}
        />
        <link rel="alternate" href={`https://renty.ai${asPath}`} hrefLang="x-default" />
        <link rel="alternate" href={`https://renty.ai${asPath}`} hrefLang="en" />
        <link rel="alternate" href={`https://renty.ai/zh${asPath}`} hrefLang="zh" />
        <link rel="alternate" href={`https://renty.ai/es${asPath}`} hrefLang="es" />
      </Head>
      <Box>
        {schemas.map(
          (schema, index) =>
            schema && (
              <script
                suppressHydrationWarning
                key={`structured-data-${schema['@type'] || index}`}
                data-testid={`seo-${schema['@type'] || index}-schema`}
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
              />
            )
        )}
      </Box>
    </>
  )
}

export default MarketPageSeo
